import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CreditCard, Target, Coins, CheckCircle2, Clock } from "lucide-react";
import { DashboardShell, PageHead } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { journeyApi, apiError } from "@/lib/journeyApi";
import { fmt } from "@/lib/format";
import { celebrate } from "@/lib/celebrate";

const input = "mt-1 w-full rounded-xl pe-surface px-3.5 py-2.5 text-base text-[#171717] outline-none focus:border-[#2F6B57] focus:ring-2 focus:ring-[#2F6B57]/25";
const numv = (v) => { const n = parseFloat(v); return Number.isFinite(n) && n > 0 ? n : 0; };

// B8: give canonical UNASSIGNED money a real job. Destinations shown are driven
// by the customer's actual current state. Every path routes through the existing
// canonical lifecycle (recommend -> accept -> confirm / confirm-build). Nothing
// is deployed until the customer confirms; partial assignment leaves the exact
// remainder still unassigned and visible.
export default function AssignUnassigned() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [step, setStep] = useState("loading"); // loading|choose|amount|plan|done
  const [dest, setDest] = useState(null); // 'debt' | 'goal'
  const [amount, setAmount] = useState("");
  const [sprint, setSprint] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [adjAvail, setAdjAvail] = useState("");
  const [adjReason, setAdjReason] = useState("Unexpected expense");

  const sources = state?.unassigned_pool?.sources || [];
  const adjSource = sources[0] || null; // v1: adjusts the oldest unassigned source
  const submitAdjust = async () => {
    if (!adjSource) return;
    const next = Math.round(Number(adjAvail) * 100) / 100;
    if (!Number.isFinite(next) || next < 0 || next > adjSource.remaining + 0.009) {
      return toast.error(`Enter an amount between $0 and ${fmt(adjSource.remaining)}.`);
    }
    setBusy(true);
    try {
      await journeyApi.adjustUnassigned(adjSource.source_id, next, adjReason);
      const fresh = await journeyApi.sprintState();
      setState(fresh);
      toast.success("Updated what's currently available. Your confirmed Edits are unchanged.");
      setStep("choose");
    } catch (e) { toast.error(apiError(e, "Could not update the available amount.")); }
    finally { setBusy(false); }
  };

  const load = async () => {
    try {
      const s = await journeyApi.sprintState();
      setState(s);
      setStep("choose");
    } catch { toast.error("Could not load your unassigned money."); navigate("/dashboard"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); }, [step]);

  const total = Math.round(((state?.unassigned_pool?.total) ?? 0) * 100) / 100;
  const primaryGoal = state?.primary_goal || null;
  const debts = (state?.foundation?.debts || []);
  const hasEligibleDebt = !!state?.current_target || debts.some((d) => !d.eliminated && Number(d.balance || 0) > 0);

  const startDest = (d) => { setDest(d); setAmount(String(total)); setStep("amount"); };

  const submitAmount = async () => {
    const amt = numv(amount);
    if (amt <= 0) return toast.error("Enter an amount to assign.");
    if (amt > total + 0.009) return toast.error(`That is more than the ${fmt(total)} you have unassigned.`);
    setBusy(true);
    try {
      const sp = await journeyApi.assignUnassigned({ amount: amt, destination: dest });
      setSprint(sp);
      setStep("plan");
    } catch (e) { toast.error(apiError(e, "Could not set up that assignment.")); }
    finally { setBusy(false); }
  };

  const confirmAssignment = async () => {
    setBusy(true);
    try {
      await journeyApi.acceptSprint(sprint.id);
      let deployed = 0;
      if (dest === "debt") {
        const payments = (sprint.allocations || []).map((a) => ({ debt_id: a.debt_id, actual_amount: a.recommended }));
        const done = await journeyApi.confirmSprint(sprint.id, payments);
        deployed = done.total_attacked || 0;
        celebrate("debt");
      } else {
        const done = await journeyApi.confirmBuild(sprint.id, sprint.build_recommended || 0);
        deployed = done.goal_confirmed || 0;
        celebrate(done.goal_completed ? "goal_complete" : "goal");
      }
      const fresh = await journeyApi.sprintState();
      setState(fresh);
      setResult({ deployed, remaining: Math.round(((fresh?.unassigned_pool?.total) ?? 0) * 100) / 100, dest });
      setStep("done");
    } catch (e) { toast.error(apiError(e, "Could not confirm this assignment.")); }
    finally { setBusy(false); }
  };

  if (step === "loading") {
    return <DashboardShell><div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-[#E5E0D9] border-t-[#2F6B57]" /></div></DashboardShell>;
  }

  // Nothing (or no longer anything) to assign.
  if (step === "choose" && total <= 0) {
    return (
      <DashboardShell>
        <button onClick={() => navigate("/dashboard")} data-testid="assign-back" className="mb-4 inline-flex items-center gap-1.5 text-sm text-[#70584A] hover:text-[#164C3B]"><ArrowLeft className="h-4 w-4" /> Back to dashboard</button>
        <div className="rounded-2xl border border-[#DDE7DF] bg-[#DDE7DF] p-6 text-center" data-testid="assign-none">
          <p className="font-serif text-2xl text-[#164C3B]">Nothing is unassigned right now.</p>
          <p className="mt-2 text-sm text-[#2F6B57]">Every dollar has a job. When your next Edit leaves money over, you can give it one here.</p>
          <Button data-testid="assign-none-dashboard" onClick={() => navigate("/dashboard")} className="mt-5 min-h-11 rounded-xl bg-[#2F6B57] px-6 font-semibold text-white hover:bg-[#255643]">Back to dashboard</Button>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <button
        onClick={() => (step === "choose" ? navigate("/dashboard") : setStep(step === "plan" ? "amount" : "choose"))}
        data-testid="assign-back"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-[#70584A] hover:text-[#164C3B]"
      >
        <ArrowLeft className="h-4 w-4" /> {step === "choose" ? "Back to dashboard" : "Back"}
      </button>

      {step === "choose" && (
        <div data-testid="assign-choose">
          <PageHead title={<>Give it a job <span className="align-middle">👀</span></>} sub="Where do you want it to go? This money is yours and still unassigned. Nothing moves until you confirm." />
          <div className="mb-5 rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9A7B3A]">Still needs a job</p>
            <p className="mt-1 font-serif text-4xl text-[#164C3B]" data-testid="assign-total">{fmt(total)}</p>
            {adjSource && (
              <button type="button" data-testid="assign-adjust-open" onClick={() => { setAdjAvail(String(adjSource.remaining)); setStep("adjust"); }}
                className="mt-2 text-sm font-medium text-[#8A7C6B] underline-offset-2 hover:text-[#164C3B] hover:underline">Amount changed? Update amount still available</button>
            )}
          </div>

          <div className="space-y-3">
            {primaryGoal && (
              <DestCard testid="assign-to-goal" icon={Target} title={`Apply to ${primaryGoal.name || "my current goal"}`}
                sub={`Put it toward your current goal${primaryGoal.percent_funded != null ? ` · ${primaryGoal.percent_funded}% funded` : ""}.`}
                onClick={() => startDest("goal")} />
            )}
            {hasEligibleDebt && (
              <DestCard testid="assign-to-debt" icon={CreditCard} title="Apply to debt"
                sub="Attack your smallest balance first, then roll to the next." onClick={() => startDest("debt")} />
            )}
            {!primaryGoal && (
              <DestCard testid="assign-choose-goal" icon={Target} title="Choose or create a new goal"
                sub="Pick a BUILD goal, then come back to fund it." onClick={() => navigate("/dashboard/goal")} />
            )}
            <button type="button" data-testid="assign-keep-unassigned" onClick={() => navigate("/dashboard")}
              className="flex w-full items-center justify-between rounded-2xl pe-surface px-5 py-4 text-left transition hover:bg-[#F5F2EC]">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F0EBE2] text-[#70584A]"><Clock className="h-5 w-5" /></span>
                <div>
                  <p className="font-serif text-lg text-[#171717]">Keep it unassigned for now</p>
                  <p className="text-sm text-[#70584A]">No problem. We'll keep it unassigned until you're ready.</p>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 shrink-0 text-[#A89A88]" />
            </button>
          </div>
        </div>
      )}

      {step === "adjust" && (
        <div data-testid="assign-adjust">
          <PageHead title="Update amount still available" sub="Life changes. Tell us what's currently available from this Edit. This updates your current amount only. It does not change your previous confirmed Edit." />
          <div className="rounded-2xl pe-surface p-6">
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">How much is still available?</label>
            <input inputMode="decimal" value={adjAvail} data-testid="assign-adjust-input"
              onChange={(e) => setAdjAvail(e.target.value.replace(/[^0-9.]/g, ""))} className={input} placeholder="$0" />
            <p className="mt-2 text-xs text-[#A89A88]">Currently available: {fmt(adjSource?.remaining || 0)}</p>
            <label className="mt-4 block text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">Reason (optional)</label>
            <select data-testid="assign-adjust-reason" value={adjReason} onChange={(e) => setAdjReason(e.target.value)} className={input}>
              <option>Life happened</option>
              <option>Unexpected expense</option>
              <option>Amount no longer available</option>
              <option>Other</option>
            </select>
            {adjSource && Number.isFinite(Number(adjAvail)) && (
              <div className="mt-5 rounded-xl border border-[#ECE6DC] bg-[#FCFBF8] p-4 text-sm" data-testid="assign-adjust-summary">
                <div className="flex justify-between"><span className="text-[#70584A]">Previously available</span><span className="tabular-nums text-[#171717]">{fmt(adjSource.remaining)}</span></div>
                <div className="flex justify-between"><span className="text-[#70584A]">Still available</span><span className="tabular-nums text-[#171717]">{fmt(Math.max(Number(adjAvail) || 0, 0))}</span></div>
                <div className="mt-1 flex justify-between border-t border-[#ECE6DC] pt-1 font-medium"><span className="text-[#70584A]">Difference</span><span className="tabular-nums text-[#B0603A]">{fmt(Math.round((Math.max(Number(adjAvail) || 0, 0) - adjSource.remaining) * 100) / 100)}</span></div>
              </div>
            )}
            <p className="mt-3 text-xs italic text-[#8A7C6B]">This updates what is currently available. It does not change your previous confirmed Edit.</p>
            <Button data-testid="assign-adjust-submit" disabled={busy} onClick={submitAdjust} className="mt-5 min-h-12 w-full rounded-xl bg-[#2F6B57] font-semibold text-white hover:bg-[#255643]">
              {busy ? "Working…" : "UPDATE AVAILABLE AMOUNT"}
            </Button>
          </div>
        </div>
      )}

      {step === "amount" && (
        <div data-testid="assign-amount">
          <PageHead title={dest === "goal" ? "How much toward your goal?" : "How much toward your debt?"} sub={`You have ${fmt(total)} unassigned. Assign all of it or just part. The rest stays unassigned.`} />
          <div className="rounded-2xl pe-surface p-6">
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">Amount to assign</label>
            <input inputMode="decimal" value={amount} data-testid="assign-amount-input"
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))} className={input} placeholder="$0" />
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-[#A89A88]">Available: {fmt(total)}</span>
              <button type="button" data-testid="assign-amount-max" onClick={() => setAmount(String(total))} className="font-medium text-[#2F6B57] hover:underline">Use all</button>
            </div>
            <Button data-testid="assign-amount-continue" disabled={busy} onClick={submitAmount} className="mt-5 min-h-12 w-full rounded-xl bg-[#2F6B57] font-semibold text-white hover:bg-[#255643]">
              {busy ? "Working…" : "Continue"} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {step === "plan" && sprint && (
        <div data-testid="assign-plan">
          <PageHead title="Here's the move." sub="This comes from money you already have. Confirm to put it to work. The Paycheck Edit never moves money for you." />
          {dest === "debt" ? (
            <div className="space-y-3" data-testid="assign-plan-debt">
              {(sprint.allocations || []).map((a) => {
                const pct = a.current_balance > 0 ? Math.round(((a.current_balance - a.projected_balance) / a.current_balance) * 100) : 0;
                return (
                  <div key={a.debt_id} className="rounded-2xl pe-surface p-5">
                    <div className="flex items-center justify-between">
                      <p className="font-serif text-xl text-[#171717]">{a.name}</p>
                      {a.eliminated && <span className="rounded-full bg-[#DDE7DF] px-3 py-1 text-xs font-semibold text-[#164C3B]">Eliminated</span>}
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm sm:grid-cols-3">
                      <div><p className="text-[#A89A88]">Current</p><p className="font-medium text-[#171717]">{fmt(a.current_balance)}</p></div>
                      <div><p className="text-[#A89A88]">Applying</p><p className="font-medium text-[#164C3B]">{fmt(a.recommended)}</p></div>
                      <div><p className="text-[#A89A88]">Projected</p><p className="font-medium text-[#171717]">{fmt(a.projected_balance)}</p></div>
                    </div>
                  </div>
                );
              })}
              {(sprint.allocations || []).length === 0 && (
                <div className="rounded-2xl border border-[#DDE7DF] bg-[#DDE7DF] p-5 text-sm text-[#2F6B57]">No eligible debt remains to apply this to.</div>
              )}
            </div>
          ) : (
            <div className="rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-6" data-testid="assign-plan-goal">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9A7B3A]">Toward your goal</p>
              <p className="mt-1.5 font-serif text-2xl text-[#164C3B]" data-testid="assign-plan-goal-amount">{fmt(sprint.build_recommended || 0)} toward {sprint.goal_name || "your goal"}</p>
              <p className="mt-1 text-sm text-[#70584A]">This contribution counts toward your goal once you confirm it.</p>
            </div>
          )}

          <div className="mt-5 space-y-3">
            <Button data-testid="assign-confirm" disabled={busy} onClick={confirmAssignment} className="min-h-12 w-full rounded-xl bg-[#2F6B57] text-base font-semibold tracking-wide text-white hover:bg-[#255643]">
              I MADE THIS MONEY MOVE
            </Button>
            <Button variant="ghost" data-testid="assign-cancel" disabled={busy} onClick={() => navigate("/dashboard")} className="min-h-11 w-full rounded-xl text-[#70584A] hover:bg-[#F5F2EC]">Not yet / keep it unassigned</Button>
          </div>
          <p className="mt-6 text-center text-sm italic text-[#8A7C6B]">Only what you confirm here updates your Paycheck Edit. The Paycheck Edit never moves money or contacts your lender.</p>
        </div>
      )}

      {step === "done" && result && (
        <div className="text-center" data-testid="assign-done">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#DDE7DF] text-[#164C3B]"><CheckCircle2 className="h-8 w-8" /></span>
          <h1 className="mt-5 font-serif text-3xl text-[#164C3B]">Edit confirmed.</h1>
          <p className="mt-2 text-[#2F6B57]">You recorded {fmt(result.deployed)} toward your {result.dest === "debt" ? "debt" : "goal"}. This confirmed action is now in The Receipts.</p>
          {result.remaining > 0 ? (
            <div className="mx-auto mt-5 max-w-sm rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-5" data-testid="assign-remaining">
              <p className="flex items-center justify-center gap-1.5 text-sm text-[#164C3B]"><Coins className="h-4 w-4 text-[#9A7B3A]" /> <span data-testid="assign-remaining-amount" className="font-semibold">{fmt(result.remaining)}</span> is still unassigned.</p>
            </div>
          ) : (
            <p className="mt-4 text-sm text-[#70584A]" data-testid="assign-remaining-none">Every unassigned dollar now has a job.</p>
          )}
          <div className="mx-auto mt-6 flex max-w-sm flex-col gap-3 sm:flex-row sm:justify-center">
            {result.remaining > 0 && (
              <Button data-testid="assign-more" onClick={() => { setResult(null); setSprint(null); setDest(null); setAmount(""); setStep("choose"); }} className="min-h-11 w-full rounded-xl bg-[#2F6B57] px-6 font-semibold text-white hover:bg-[#255643] sm:w-auto">Assign more</Button>
            )}
            <Button variant="outline" data-testid="assign-done-dashboard" onClick={() => navigate("/dashboard")} className="min-h-11 w-full rounded-xl border-[#E5E0D9] px-6 sm:w-auto">Back to dashboard</Button>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

function DestCard({ testid, icon: Icon, title, sub, onClick }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className="flex w-full items-center justify-between rounded-2xl border-2 border-[#E5E0D9] bg-white px-5 py-4 text-left transition hover:border-[#2F6B57] hover:bg-[#DDE7DF]/40">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DDE7DF] text-[#164C3B]"><Icon className="h-5 w-5" /></span>
        <div>
          <p className="font-serif text-lg text-[#164C3B]">{title}</p>
          <p className="text-sm text-[#70584A]">{sub}</p>
        </div>
      </div>
      <ArrowRight className="h-5 w-5 shrink-0 text-[#2F6B57]" />
    </button>
  );
}
